import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Media from 'react-media';

import useBasketContext from '@hooks/useBasketContext';
import usePostcodeContext from '@hooks/usePostcodeContext';

import { Button, Icon, Banner } from '@components/Atoms';
import { ButtonType } from '@components/Atoms/Button/types';
import { Logo } from '@components/Atoms/Logo/Logo';
import { MiniBasketPopup, SalesCountdown } from '@components/Organisms';

import Close from '@public/icons/Close-white.svg';
import HamburgerBlack from '@public/icons/Hamburger-Menu-Dark.svg';
import BasketHeaderIconBlack from '@public/icons/Basket-Header-Black.svg';

import { StyledHeader, HeaderButton, HeaderMenu, HeaderDropDown } from './styled';
import { breakpoints } from '@theme/breakpoints';
import useGAEvent from '@hooks/useGAEvent';
import PhoneIcon from '@public/icons/Phone.svg';
import PhoneWhiteIcon from '@public/icons/Phone-White.svg';
import { HeaderProps } from './types';
import { useRouter } from 'next/router';

const GetHBButton = () => {
  return (
    <Link href="/refer-a-friend" passHref>
      <Button as="a" buttonType={ButtonType.PRIMARY} className="get-HB flex">
        Refer a friend
      </Button>
    </Link>
  );
};

const BasketButton = () => {
  const { basketItem: basketItemId, setIsBasketPopupOpen, isBasketPopupOpen } = useBasketContext();

  const gaEvent = useGAEvent();

  useEffect(() => {
    if (isBasketPopupOpen) {
      gaEvent.send({
        action: 'view_cart',
      });
    }
  }, [gaEvent, isBasketPopupOpen]);

  return (
    <div className="header__basket-button">
      {basketItemId && (
        <button onClick={() => setIsBasketPopupOpen((prev) => !prev)}>
          <Icon sm icon={BasketHeaderIconBlack} />
        </button>
      )}
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({ path = null, showCtaInHeader = true, header }) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [pinned, setPinned] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const { basketItem } = useBasketContext();
  const { isCurrentPostcodeEligible } = usePostcodeContext();
  const ref = useRef(null);
  const dropdown = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    setOpenDropdown(null);
  }, [router.asPath]);

  return (
    <>
      <StyledHeader
        onUnfix={() => {
          if (pinned) {
            setPinned(false);
          }
        }}
        onUnpin={() => {
          setPinned(false);
        }}
        onPin={() => {
          setPinned(true);
        }}
        downTolerance={5}
      >
        <Banner className="header-banner" />
        <SalesCountdown />

        <div className="container header__container ">
          <Logo />
          <nav className="header__navigation">
            {header?.contactUs && (
              <Link href={`tel:${header?.contactUs.phone.replace(' ', '')}`} passHref>
                <HeaderButton as="a" color="var(--colors-primary)">
                  <div className="primary-phone">
                    <span>{header?.contactUs.text}</span>
                    <Icon xxs icon={PhoneIcon} />
                    <span>{header?.contactUs.phone}</span>
                  </div>
                </HeaderButton>
              </Link>
            )}

            {header &&
              header?.menu?.length > 0 &&
              header?.menu.map(({ text, link, submenu }, index) => (
                <div
                  role="button"
                  key={`header-menu-${index}`}
                  className="relative header__more"
                  onMouseEnter={() => setOpenDropdown(index)}
                  onMouseLeave={() => setOpenDropdown(null)}
                  tabIndex={0}
                  onFocus={() => setOpenDropdown(index)}
                  onBlur={() => setOpenDropdown(null)}
                >
                  {link ? (
                    <Link key={index} href={link} passHref>
                      <HeaderButton as="a">
                        <span className={`${link === path ? 'font-bold' : ''}`}>{text}</span>
                      </HeaderButton>
                    </Link>
                  ) : (
                    <HeaderButton as="span">{text}</HeaderButton>
                  )}

                  {submenu.length > 0 && (
                    <HeaderDropDown
                      ref={dropdown}
                      className="header-dropdown"
                      style={{
                        display: openDropdown === index ? 'block' : 'none',
                      }}
                    >
                      <div className="header-dropdown__wrapper">
                        {submenu?.map(({ text, link }, subIndex) => (
                          <Link key={`header-submenu-${subIndex}`} href={link} passHref>
                            <a href={link}>{text}</a>
                          </Link>
                        ))}
                      </div>
                    </HeaderDropDown>
                  )}
                </div>
              ))}
          </nav>
          {showCtaInHeader && header?.contactUs && (
            <div className="header__aside">
              <a href={`tel:${header?.contactUs.phone.replace(' ', '')}`}>
                <>
                  <span>{header?.contactUs.text}?</span>
                  <Icon xxs icon={PhoneIcon} />
                  <span>{header?.contactUs.phone}</span>
                </>
              </a>
              {basketItem ? <BasketButton /> : <GetHBButton />}
              <MiniBasketPopup />
            </div>
          )}

          <div className="header__hamburger">
            <Media queries={breakpoints}>
              {({ tabletMax }) =>
                tabletMax &&
                basketItem &&
                isCurrentPostcodeEligible &&
                showCtaInHeader && <BasketButton />
              }
            </Media>
            <button onClick={() => setMenuOpen(true)} aria-pressed="false">
              <Icon icon={HamburgerBlack} sm altText="Open menu" />
            </button>
          </div>
        </div>

        <HeaderMenu open={menuOpen} ref={ref}>
          <div className="container mobile-navigation__header">
            <Logo />
            <div className="mobile-navigation__additional">
              {header?.contactUs && (
                <a href={`tel:${header?.contactUs.phone.replace(' ', '')}`}>
                  <>
                    <Icon xxs icon={PhoneWhiteIcon} />
                    {header?.contactUs.phone}
                  </>
                </a>
              )}
              <button onClick={() => setMenuOpen(false)} className="mobile-navigation__close">
                <Icon icon={Close} altText="Close menu" sm />
              </button>
            </div>
          </div>

          <nav className="container mobile-navigation">
            {header?.menu?.map((menuItem, index) => (
              <div className="container mobile-navigation" key={`mobile-menu-${index}`}>
                <Link href={menuItem.link} passHref>
                  <a
                    className="mobile-navigation__link"
                    onClick={() => setMenuOpen(false)}
                    href={menuItem.link}
                  >
                    {menuItem.text}
                  </a>
                </Link>
                {menuItem.submenu?.length > 0 && (
                  <div className="container mobile-navigation__submenu">
                    {menuItem.submenu.map((submenuItem, subIndex) => (
                      <Link key={`mobile-submenu-${subIndex}`} href={submenuItem.link} passHref>
                        <a
                          className="mobile-navigation__link"
                          onClick={() => setMenuOpen(false)}
                          href={submenuItem.link}
                        >
                          {submenuItem.text}
                        </a>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </HeaderMenu>
      </StyledHeader>
    </>
  );
};
